import { List, Typography } from 'antd';
import { forwardRef, memo, useImperativeHandle } from 'react';
import { aiAgentCenterMessages } from 'messages';
import { useAIAgentCenterContext } from 'contexts/ai-agent-center';
import AICallHistoryItem from './AICallHistoryItem';
import { useLazyOmniCallSessionHistoriesList } from 'hooks';
import dayjs from 'dayjs';
import { InView } from 'react-intersection-observer';
import { useEffect } from 'react';

type AICallHistoryListProps = {};

export type AICallHistoryListRefProps = {
  handleRefetchCallHistory: () => void;
};

const AICallHistoryList = forwardRef<AICallHistoryListRefProps, AICallHistoryListProps>((props, ref) => {
  useImperativeHandle(ref, () => ({
    handleRefetchCallHistory: () => {
      handleFetchData();
    }
  }));

  const { callSession, setCallSession, filterForm } = useAIAgentCenterContext();

  const { callSource, callDateRange, keywordCall } = filterForm || {};

  const { data, handleLoadMore, hasMore, count, isLoading, fetchData } = useLazyOmniCallSessionHistoriesList();

  useEffect(() => {
    handleFetchData();
  }, [callSource, keywordCall, callDateRange?.[0], callDateRange?.[1]]);

  const handleFetchData = () => {
    fetchData({
      callSource,
      isHandleByAi: true,
      fromStartTime: callDateRange?.[0] ? dayjs(callDateRange[0]).startOf('day').toISOString() : undefined,
      toEndTime: callDateRange?.[1] ? dayjs(callDateRange[1]).endOf('day').toISOString() : undefined,
      keyword: keywordCall ?? ''
    });
  };

  return (
    <div className='h-full max-h-full w-full overflow-y-auto'>
      <div className='flex h-10 items-center px-5'>
        <Typography.Text
          onClick={() => {
            handleLoadMore();
          }}
          className='text-xs text-colorTextPlaceholder'
        >
          {aiAgentCenterMessages.callCount(count)}
        </Typography.Text>
      </div>
      <List
        key={'call-history-list'}
        className='w-full pb-5'
        loading={isLoading}
        dataSource={data}
        renderItem={(item) => (
          <AICallHistoryItem
            item={item}
            selected={callSession?.callSessionId === item.callSessionId}
            onSelect={() => {
              setCallSession(item);
            }}
          />
        )}
        rowKey={'callSessionId'}
        footer={
          hasMore ? (
            <InView
              className='-mt-3'
              onChange={(open) => {
                if (open) {
                  handleLoadMore();
                }
              }}
            >
              <div className='flex h-18 gap-3 px-5 py-3'>
                <div>
                  <div className='skeleton-active h-12 w-12 rounded-full'></div>
                </div>
                <div className='mt-[3px] flex flex-1 flex-col gap-1'>
                  <div className='skeleton-active h-5 w-[180px] rounded-sm'></div>
                  <div className='skeleton-active h-[18px] w-[100px] rounded-sm'></div>
                </div>
                <div className='mt-[3px] flex h-full flex-col items-end gap-1.5'>
                  <div className='skeleton-active h-[14px] w-[40px] rounded-sm'></div>
                  <div className='skeleton-active h-[14px] w-[30px] rounded-sm'></div>
                </div>
              </div>
            </InView>
          ) : null
        }
      />
    </div>
  );
});

export default memo(AICallHistoryList);
